import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Env from "@/config/environment";
import { useTranslation } from "@/features/utils/i18n";
import { Trans } from "react-i18next";
import { Copy } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";

// Identity secret for authenticated in-app embeds. The merchant's backend signs
// HMAC-SHA256(email-or-phone) with this secret and passes it as data-user-hash so
// the widget can trust the logged-in user's contact. Never expose it in the browser.
export function IdentitySecret({ secret }: { secret: string }) {
  const { t } = useTranslation("connectors");
  const [shown, setShown] = useState(false);
  return (
    <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
      <Label className="text-sm font-semibold">
        {t("connectors.form.identitySecretTitle", "Secret d'identité (in-app)")}
      </Label>
      <p className="mb-1 text-xs text-muted-foreground">
        {t(
          "connectors.form.identitySecretHint",
          "Pour un widget affiché à un utilisateur déjà connecté : signez HMAC-SHA256(email — ou téléphone) avec ce secret côté serveur et passez-le en data-user-hash (avec data-user-email / data-user-phone). Gardez-le secret (backend uniquement).",
        )}
      </p>
      <div className="flex items-center gap-2">
        <Input
          readOnly
          type={shown ? "text" : "password"}
          value={secret}
          className="font-mono text-xs"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setShown((s) => !s)}
        >
          {shown
            ? t("connectors.form.hide", "Masquer")
            : t("connectors.form.show", "Afficher")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            navigator.clipboard.writeText(secret);
            toast.success(t("connectors.form.webSnippetCopied", "Copié"));
          }}
        >
          <Copy className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

/**
 * The "Rappelez-moi" call, with its URL written out.
 *
 * The URL is the whole point of showing this. It is **absolute, on the API host** — a
 * `fetch("/api/public/v1/callback")` from the site's own pages goes to that site's own
 * server (a CDN, for most), which answers the pre-flight with its 404 page and no CORS
 * header; the browser then reports a CORS error, and the real cause — a request that
 * never reached us — is nowhere in that message. It cost us an afternoon on our own site.
 */
export function CallbackSnippet({ connectorId }: { connectorId: string }) {
  const { t } = useTranslation("connectors");
  const snippet = `await fetch("${Env.server}/api/public/v1/callback", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    connector: "${connectorId}",
    phone_number: "+33612345678", // ${t("connectors.form.callbackSnippetFormat")}
  }),
});`;

  return (
    <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
      <Label className="text-sm font-semibold">
        {t("connectors.form.callbackSnippetTitle")}
      </Label>
      <p className="mb-1 text-xs text-muted-foreground">
        <Trans
          i18nKey="connectors.form.callbackSnippetDescription"
          components={{
            ringing: <code>{`{ "status": "ringing" }`}</code>,
            code: <code />,
          }}
        />
      </p>
      <div className="flex items-start gap-2">
        <pre className="flex-1 overflow-x-auto whitespace-pre rounded-md bg-background p-3 font-mono text-xs">
          {snippet}
        </pre>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            navigator.clipboard.writeText(snippet);
            toast.success(t("connectors.form.webSnippetCopied", "Copié"));
          }}
        >
          <Copy className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

// Install snippet for a web connector: the one-liner customers paste on their site.
// The embed.js is served from the app origin; the connector id is the public embed key.
export function WebEmbedSnippet({
  connectorId,
  clientId,
}: {
  connectorId: string;
  clientId?: string;
}) {
  const { t } = useTranslation("connectors");
  const appOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const snippet = `<script
  src="${appOrigin}/embed.js"
  data-connector="${connectorId}"
  data-position="right"
  defer></script>`;

  return (
    <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
      <Label className="text-sm font-semibold">
        {t("connectors.form.webSnippetTitle", "Script d'installation")}
      </Label>
      <p className="mb-1 text-xs text-muted-foreground">
        {t(
          "connectors.form.webSnippetDescription",
          "Collez ce script avant </body> sur votre site pour afficher le chat.",
        )}
      </p>
      <div className="flex items-start gap-2">
        <pre className="flex-1 overflow-x-auto rounded-md bg-background p-3 text-xs font-mono whitespace-pre">
          {snippet}
        </pre>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            navigator.clipboard.writeText(snippet);
            toast.success(t("connectors.form.webSnippetCopied", "Copié"));
          }}
        >
          <Copy className="h-3 w-3" />
        </Button>
      </div>
      {clientId && (
        <a
          href={`/${clientId}/settings/connectors/personalization`}
          className="inline-block text-xs text-primary underline"
        >
          {t(
            "connectors.form.webCustomizeLink",
            "Personnaliser l'apparence (logo, couleur, arrondi, ombre)",
          )}
        </a>
      )}
    </div>
  );
}
